/**
 * Local-notification scheduler — sleep sweet spots, reminders, daily
 * notices and timer guards (Sleep Suite v1.2.0, extended in v1.3.0).
 *
 * Native-only: every entry point resolves `@capacitor/local-notifications`
 * via dynamic `await import()` inside try/catch, so web builds and web
 * runtime degrade to silent no-ops. Per the thenable-proxy rule the plugin
 * handle is only ever kept inside a plain wrapper object — it is never
 * awaited and never returned from an async function.
 *
 * Stable numeric ID ranges make every reschedule idempotent
 * (cancel the whole range, then schedule fresh). Never overlap them:
 *
 *   9100–9139  sweet spots — 15 min before each predicted nap start + bedtime
 *              (Premium; pref napReminders; route /sleep)
 *   9200       feed reminder — feedReminderMinutes after the last feed
 *              (pref reminders.feeds; route /feed)
 *   9300–9339  medication doses (pref reminders.medications; route /medications)
 *   9400–9439  vaccination due reminders — 09:00 local on each due date
 *              (pref reminders.vaccinations; route /vaccinations)
 *   9500       still-asleep check-in — open nap running longer than
 *              max(150 min, 1.5 × avgNapMinutes) (Premium; pref sleepCheckIn;
 *              quiet: skip; route /sleep)
 *   9510–9513  timer-left-running guards — open sleep > 12 h → 9510,
 *              FEED/FEED_DUAL > 2 h → 9511, PUMPING > 2 h → 9512,
 *              ACTIVITY > 3 h → 9513 (pref timerGuards; quiet: defer;
 *              routes /sleep /feed /pumping /activity)
 *   9600       nappy gap — age-aware gap after the last change
 *              (opt-in pref reminders.nappies; quiet: skip; route /nappy)
 *   9601       16:00 hydration nudge — only when a nappy has been logged in
 *              the last 24 h AND today's wet+both count is below half the
 *              expected number (same pref; route /nappy)
 *   9700       pumping reminder — pumpReminderMinutes after the last pump
 *              (opt-in pref reminders.pumping; quiet: skip; route /pumping)
 *   9800       daily evening recap at a user-chosen time (pref dailyRecap
 *              { enabled, time }; skipped when today has no events; bypasses
 *              quiet hours; route /)
 *   9810–9816  daily tip, 7 days pre-scheduled at a user-chosen time (pref
 *              dailyTip { enabled, time }; pickTipOfTheDay; bypasses quiet
 *              hours; route /tips/<tipId>)
 *   9900       growth check-in — 10:00 local when the last measurement is
 *              older than 28 d (< 6 mo) / 56 d (6–12 mo) / 90 d (12 mo+)
 *              (pref reminders.growth; quiet: defer; route /growth)
 *   9910–9913  NHS health-review heads-up — 09:00 local, 3 days before
 *              6 weeks / 9 months / 24 months (under reminders.vaccinations;
 *              quiet: defer; route /vaccinations)
 *   9950       weekly wellbeing check-in — Sunday 20:30 local (opt-in pref
 *              moodCheckIn; skipped when a mood entry exists for today;
 *              quiet: skip; route /mood)
 *   9960       trial offer — one-off, 30 min after the onboarding paywall is
 *              closed without a purchase (v1.5.0; consent-only pref offers;
 *              quiet: defer; route /offer). Scheduled and cancelled by
 *              features/offer/trialOffer.js, never by the driver.
 *   9961       second ask — one-off, day 8 at 10:00 local, quiet: defer; route
 *              /paywall?src=day8-notification&reason=week (v1.5.1; scheduled
 *              with 9960 under the same consent-only pref offers). Scheduled
 *              and cancelled by features/offer/trialOffer.js, withdrawn by
 *              useSecondAsk once the in-app ask has shown; never by the driver.
 *
 * Every notification carries `extra: { route }` so a tap can deep-link; the
 * SleepSuiteServices driver registers the single tap listener.
 *
 * Quiet hours: 'skip' schedulers drop a notification that would fire inside
 * the window; 'defer' schedulers move it to the end of the window
 * (`deferPastQuietHours`); user-chosen times (recap, daily tip) bypass it.
 *
 * Permission is requested ONLY from an explicit user tap: a toggle in
 * NotificationPreferences / the Tips page (`requestPermission`), or the
 * "Turn on notifications" button of the baby-creation prompt
 * (`enableRecommendedNotifications`, which also switches on the
 * RECOMMENDED_PREFS_PATCH set — v1.4.2). Never on launch, never as a side
 * effect of a data mutation. All scheduling paths check silently
 * (`checkPermission`) and bail out when not granted.
 *
 * Promotional notifications (the 9960 trial offer and the 9961 second ask, App
 * Store Guideline 4.5.4) need explicit consent: `offers` defaults to false and
 * is switched on only by its own toggle — the onboarding notifications step or
 * Settings → Notifications → Offers. It is deliberately NOT in
 * RECOMMENDED_PREFS_PATCH.
 *
 * Reschedules are driven from a single subscriber — SleepSuiteServices,
 * mounted once in Layout — which reacts to schedule query success, event
 * data changes, app resume, and preference changes (via subscribePrefs).
 */

import { addDays, addMonths, differenceInDays, format } from 'date-fns';
import { isNative } from '../shared/utils/platform';
import { formatTime, formatDurationShort } from '../shared/utils/dateHelpers';
// Read-only import of the vaccination feature's pure schedule helpers — the
// NHS schedule table and due/done matching live there, never duplicated here.
import { nhsSchedule, matchVaccinationsToSchedule } from '../features/growth/vaccinationSchedule';
// Read-only imports of other features' pure helpers (age-aware nappy gap,
// sleep durations, deterministic tip-of-the-day) — never duplicated here.
import { getExpectedWetNappies, getHydrationStatus } from '../features/nappy/hydrationThresholds';
import { isSleep, sleepDurationMinutes } from '../features/sleep/sleepShape';
import { openSleepBelongsToDay } from '../features/sleep/scheduleRows';
import { pickTipOfTheDay } from '../features/tips/useTips';
import tipsCatalogue from '../shared/data/tips.json';

// --- Stable ID ranges (per docs/sleep-suite-spec.md + ux-modernisation-spec §9 — do not change) ---
export const SWEET_SPOT_ID_START = 9100;
export const SWEET_SPOT_ID_END = 9139;
export const FEED_REMINDER_ID = 9200;
export const MED_ID_START = 9300;
export const MED_ID_END = 9339;
export const VACC_ID_START = 9400;
export const VACC_ID_END = 9439;
export const SLEEP_CHECKIN_ID = 9500;
export const TIMER_GUARD_ID_START = 9510;
export const TIMER_GUARD_ID_END = 9513;
export const TIMER_GUARD_SLEEP_ID = 9510;
export const TIMER_GUARD_FEED_ID = 9511;
export const TIMER_GUARD_PUMP_ID = 9512;
export const TIMER_GUARD_ACTIVITY_ID = 9513;
export const NAPPY_GAP_ID = 9600;
export const HYDRATION_NUDGE_ID = 9601;
export const PUMP_REMINDER_ID = 9700;
export const DAILY_RECAP_ID = 9800;
export const DAILY_TIP_ID_START = 9810;
export const DAILY_TIP_ID_END = 9816;
export const GROWTH_CHECKIN_ID = 9900;
export const HEALTH_REVIEW_ID_START = 9910;
export const HEALTH_REVIEW_ID_END = 9913;
export const MOOD_CHECKIN_ID = 9950;
export const TRIAL_OFFER_ID = 9960;
export const SECOND_ASK_ID = 9961;

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const SWEET_SPOT_LEAD_MINUTES = 15;
const VACC_WINDOW_DAYS = 60; // only schedule due dates within the next 60 days
const VACC_FIRE_HOUR = 9; // 09:00 local on the due date
const SLEEP_CHECKIN_MIN_MINUTES = 150;
const SLEEP_CHECKIN_AVG_FACTOR = 1.5;
const TIMER_GUARD_LIMITS_MS = {
  SLEEP: 12 * HOUR,
  FEED: 2 * HOUR,
  FEED_DUAL: 2 * HOUR,
  PUMPING: 2 * HOUR,
  ACTIVITY: 3 * HOUR,
};
const NAPPY_GAP_MIN_MINUTES = 180;
const NAPPY_GAP_MAX_MINUTES = 360;
const HYDRATION_NUDGE_HOUR = 16;
const DAILY_TIP_DAYS = 7;
const GROWTH_FIRE_HOUR = 10;
const HEALTH_REVIEW_LEAD_DAYS = 3;
const HEALTH_REVIEW_FIRE_HOUR = 9;
const MOOD_CHECKIN_DAY = 0; // Sunday
const MOOD_CHECKIN_TIME = { hour: 20, minute: 30 };
const OPEN_SLEEP_MAX_AGE_MS = 16 * HOUR; // mirrors sleepShape.OPEN_MAX_MS

// --- Preferences (localStorage-backed, same key the old page used) ---

const PREFS_KEY = 'bt_notification_prefs';
const TIPS_SEEN_KEY = 'bt_tips_seen'; // owned by features/tips/useTips — read-only here
const GROWTH_NUDGE_KEY_PREFIX = 'bt_growth_nudged:'; // <babyId> → "<lastKey>|<fireAtISO>"

const DEFAULT_PREFS = {
  quietStart: '22:00',
  quietEnd: '07:00',
  reminders: {
    feeds: true,
    medications: true,
    vaccinations: true,
    nappies: false,
    pumping: false,
    growth: true,
  },
  // Sweet-spot nap & bedtime reminders (Premium). Off until the user
  // explicitly toggles on — that toggle is where permission is requested.
  napReminders: false,
  // Still-asleep check-in (Premium) — a nap running much longer than usual.
  sleepCheckIn: true,
  // Timer-left-running guards (free).
  timerGuards: true,
  // Weekly wellbeing check-in (opt-in).
  moodCheckIn: false,
  // Minutes after the last feed / pump before the reminder fires.
  feedReminderMinutes: 180,
  pumpReminderMinutes: 180,
  // User-chosen-time notices (opt-in). Times are 'HH:mm' local.
  dailyRecap: { enabled: false, time: '20:00' },
  dailyTip: { enabled: false, time: '09:00' },
  // Offers such as the free-trial reminder (v1.5.0). Promotional, so off until
  // the user consents with its own toggle — nothing switches it on silently.
  offers: false,
};

function defaultPrefs() {
  return {
    ...DEFAULT_PREFS,
    reminders: { ...DEFAULT_PREFS.reminders },
    dailyRecap: { ...DEFAULT_PREFS.dailyRecap },
    dailyTip: { ...DEFAULT_PREFS.dailyTip },
  };
}

/** Current preferences, deep-merged over the defaults (new keys are safe). */
export function getPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return defaultPrefs();
    const parsed = JSON.parse(raw) || {};
    return {
      ...DEFAULT_PREFS,
      ...parsed,
      reminders: { ...DEFAULT_PREFS.reminders, ...(parsed.reminders || {}) },
      dailyRecap: { ...DEFAULT_PREFS.dailyRecap, ...(parsed.dailyRecap || {}) },
      dailyTip: { ...DEFAULT_PREFS.dailyTip, ...(parsed.dailyTip || {}) },
    };
  } catch {
    return defaultPrefs();
  }
}

const prefsListeners = new Set();

export function savePrefs(next) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — prefs just won't persist */
  }
  prefsListeners.forEach((fn) => {
    try {
      fn(getPrefs());
    } catch {
      /* one bad listener must not break the rest */
    }
  });
}

/** Subscribe to preference changes; returns an unsubscribe function. */
export function subscribePrefs(fn) {
  prefsListeners.add(fn);
  return () => prefsListeners.delete(fn);
}

// --- Recommended set (baby-creation prompt, v1.4.2) ---

/**
 * What the "Turn on notifications" prompt switches on when a baby is created.
 * The still-asleep check-in and nap reminders are Premium in the settings UI,
 * but a new account is inside its 7-day full-access preview, so they belong
 * here. The opt-in extras (pumping, weekly wellbeing) stay as they are, and
 * quiet hours / reminder intervals are never touched. `dailyTip` and
 * `dailyRecap` only flip `enabled` — any time the user has set is kept.
 */
export const RECOMMENDED_PREFS_PATCH = Object.freeze({
  napReminders: true,
  sleepCheckIn: true,
  timerGuards: true,
  reminders: Object.freeze({
    feeds: true,
    medications: true,
    vaccinations: true,
    nappies: true,
    growth: true,
  }),
  dailyTip: Object.freeze({ enabled: true }),
  dailyRecap: Object.freeze({ enabled: true }),
});

/**
 * Pure: RECOMMENDED_PREFS_PATCH deep-merged over `current`, keeping every
 * other key (quiet hours, intervals, moodCheckIn, reminders.pumping, chosen
 * times). Never mutates the input; a missing time falls back to the default.
 * `entitled: false` (a lapsed free account adding another baby) leaves the
 * Premium pair — napReminders and sleepCheckIn — exactly as they were, so the
 * prompt never records a preference the driver would silently ignore.
 */
export function applyRecommendedPrefs(current, { entitled = true } = {}) {
  const base = current && typeof current === 'object' ? current : {};
  const withEnabled = (existing, fallbackTime) => ({
    ...(existing || {}),
    time: existing?.time || fallbackTime,
    enabled: true,
  });
  const premiumPair = entitled
    ? {
        napReminders: RECOMMENDED_PREFS_PATCH.napReminders,
        sleepCheckIn: RECOMMENDED_PREFS_PATCH.sleepCheckIn,
      }
    : {};
  return {
    ...base,
    ...premiumPair,
    timerGuards: RECOMMENDED_PREFS_PATCH.timerGuards,
    reminders: { ...(base.reminders || {}), ...RECOMMENDED_PREFS_PATCH.reminders },
    dailyTip: withEnabled(base.dailyTip, DEFAULT_PREFS.dailyTip.time),
    dailyRecap: withEnabled(base.dailyRecap, DEFAULT_PREFS.dailyRecap.time),
  };
}

// --- Plugin access (dynamic import, wrapper object per the proxy rule) ---

let cachedPlugin = null; // { api } | null — plain wrapper, never the raw proxy

async function getPlugin() {
  if (!isNative()) return null;
  if (cachedPlugin) return cachedPlugin;
  try {
    const mod = await import('@capacitor/local-notifications');
    if (!mod?.LocalNotifications) return null;
    cachedPlugin = { api: mod.LocalNotifications };
    return cachedPlugin;
  } catch {
    return null; // plugin not installed (web build) — degrade silently
  }
}

/** True when notification permission is already granted. Never prompts. */
export async function checkPermission() {
  const plugin = await getPlugin();
  if (!plugin) return false;
  try {
    const status = await plugin.api.checkPermissions();
    return status?.display === 'granted';
  } catch {
    return false;
  }
}

/**
 * Prompt for notification permission. Call ONLY from an explicit user
 * toggle — never on launch. Returns true when granted.
 */
export async function requestPermission() {
  const plugin = await getPlugin();
  if (!plugin) return false;
  try {
    const current = await plugin.api.checkPermissions();
    if (current?.display === 'granted') return true;
    const status = await plugin.api.requestPermissions();
    return status?.display === 'granted';
  } catch {
    return false;
  }
}

/**
 * Turn on the recommended set from the baby-creation prompt. This prompts
 * for permission, so call it ONLY from the prompt's explicit
 * "Turn on notifications" tap. Once granted it saves
 * applyRecommendedPrefs(getPrefs()) — the SleepSuiteServices driver then
 * schedules from the new prefs. Resolves
 *   { ok: true } | { ok: false, reason: 'unsupported' | 'denied' }
 * On web (no plugin) it resolves 'unsupported' without touching prefs.
 */
export async function enableRecommendedNotifications({ entitled = true } = {}) {
  const plugin = await getPlugin();
  if (!plugin) return { ok: false, reason: 'unsupported' };
  const granted = await requestPermission();
  if (!granted) return { ok: false, reason: 'denied' };
  savePrefs(applyRecommendedPrefs(getPrefs(), { entitled }));
  return { ok: true };
}

/**
 * Listen for taps on local notifications. `onAction(route, notification)`
 * receives the `extra.route` the notification was scheduled with. Resolves
 * with an unsubscribe function (a no-op on web). The plugin handle itself is
 * never returned — only the listener handle's `remove` is wrapped.
 */
export async function addActionListener(onAction) {
  const plugin = await getPlugin();
  if (!plugin) return () => {};
  try {
    const handle = await plugin.api.addListener('localNotificationActionPerformed', (event) => {
      const notification = event?.notification || {};
      const route = notification.extra?.route;
      try {
        onAction(typeof route === 'string' ? route : null, notification);
      } catch {
        /* a bad handler must not break the listener */
      }
    });
    return () => {
      try {
        handle.remove();
      } catch {
        /* already removed */
      }
    };
  } catch {
    return () => {};
  }
}

// --- Helpers ---

const toMs = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return Date.parse(value);
};

const toMins = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** Date for `hhmm` ('HH:mm') on the same local day as `day`. */
function atLocalTime(day, hhmm, fallback = { hour: 9, minute: 0 }) {
  const d = new Date(day);
  const parts = String(hhmm || '').split(':').map(Number);
  const hour = Number.isFinite(parts[0]) ? parts[0] : fallback.hour;
  const minute = Number.isFinite(parts[1]) ? parts[1] : fallback.minute;
  d.setHours(hour, minute, 0, 0);
  return d;
}

/** True when `date` falls inside the quiet-hours window (handles overnight). */
export function isInQuietHours(date, prefs) {
  const { quietStart, quietEnd } = prefs || {};
  if (!quietStart || !quietEnd || quietStart === quietEnd) return false;
  const mins = date.getHours() * 60 + date.getMinutes();
  const start = toMins(quietStart);
  const end = toMins(quietEnd);
  return start < end ? mins >= start && mins < end : mins >= start || mins < end;
}

/**
 * 'defer' semantics: a date inside the quiet-hours window moves to the end of
 * that window (the same day's quietEnd, or the next day's when it has already
 * passed). Dates outside the window are returned unchanged.
 */
export function deferPastQuietHours(date, prefs) {
  if (!isInQuietHours(date, prefs)) return date;
  const end = atLocalTime(date, prefs.quietEnd, { hour: 7, minute: 0 });
  if (end <= date) end.setDate(end.getDate() + 1);
  return end;
}

async function cancelRange(api, startId, endId) {
  const notifications = [];
  for (let id = startId; id <= endId; id++) notifications.push({ id });
  try {
    await api.cancel({ notifications });
  } catch {
    /* cancelling never-scheduled ids is fine */
  }
}

async function scheduleAll(api, notifications) {
  if (!notifications || notifications.length === 0) return;
  try {
    await api.schedule({ notifications });
  } catch {
    /* scheduling failure is non-fatal */
  }
}

const eventType = (e) => e?.entityType || e?.type;

/** Age in whole weeks on `atDate` (null without a date of birth). */
function ageWeeksAt(dob, atDate) {
  const born = toMs(dob);
  if (Number.isNaN(born)) return null;
  return Math.max(0, Math.floor((atDate.getTime() - born) / (7 * DAY)));
}

// --- Schedulers (cancel range + schedule = idempotent) ---

/**
 * The predicted events a sweet-spot reminder may still fire for (pure).
 *
 * `openSleep` is the client's running sleep ({ startTime, data.category }).
 * The schedule is computed server-side and cannot refresh while the phone
 * is offline, so it may still predict a bedtime for a night sleep that has
 * already begun — and the reminder used to fire anyway ("Start winding down
 * — bedtime is around 19:30" fifteen minutes after the baby went down,
 * v1.4.3 bug 2). Only a sleep that belongs to the schedule's day counts
 * (openSleepBelongsToDay — the same rule the card uses): a night sleep
 * running since the evening before, seen at a 03:00 feed, must not wipe
 * today's nap reminders. While a NIGHT sleep is open nothing is scheduled;
 * while a NAP is open the nap it IS — the first predicted nap not yet taken
 * (exactly what the card folds it into) unless the server already shows a
 * nap running, plus any predicted nap starting at or before it — is not a
 * target: a nap that began 35 minutes before its predicted window must not
 * be reminded about twenty minutes in.
 */
export function sweetSpotTargets(schedule, openSleep = null) {
  if (!schedule) return [];
  const naps = schedule.naps || [];
  const openStart = Date.parse(openSleep?.startTime || '');
  const openCategory = openSleep?.data?.category;
  const open = Number.isFinite(openStart) && openSleepBelongsToDay(openSleep, schedule);
  if (open && openCategory === 'night') return [];
  const serverShowsRunningNap = naps.some((nap) => nap.inProgress);
  let runningIndex = -1;
  if (open && !serverShowsRunningNap) runningIndex = naps.findIndex((nap) => !nap.taken);
  const targets = [];
  naps.forEach((nap, i) => {
    if (nap.taken || !nap.start) return;
    if (open && (i === runningIndex || Date.parse(nap.start) <= openStart)) return;
    targets.push({ label: nap.label || `Nap ${nap.index}`, start: nap.start, isBedtime: false });
  });
  if (schedule.bedtime?.start && !schedule.bedtime.taken) {
    targets.push({ label: 'Bedtime', start: schedule.bedtime.start, isBedtime: true });
  }
  return targets;
}

/**
 * Sweet-spot reminders: 15 min before each remaining predicted nap start and
 * bedtime, skipping past times and quiet hours. Pass `enabled: false` (free
 * user, toggle off) to just clear the range. `openSleep` — see
 * sweetSpotTargets.
 */
export async function rescheduleSweetSpots({ schedule, babyName, enabled, openSleep = null }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, SWEET_SPOT_ID_START, SWEET_SPOT_ID_END);
  if (!enabled || !schedule) return;
  if (!(await checkPermission())) return;

  const prefs = getPrefs();
  const targets = sweetSpotTargets(schedule, openSleep);

  const now = new Date();
  const name = babyName || 'Baby';
  const notifications = [];
  targets.forEach((target, i) => {
    const id = SWEET_SPOT_ID_START + i;
    if (id > SWEET_SPOT_ID_END) return;
    const fireAt = new Date(new Date(target.start).getTime() - SWEET_SPOT_LEAD_MINUTES * MINUTE);
    if (fireAt <= now) return; // skip past times
    if (isInQuietHours(fireAt, prefs)) return;
    notifications.push({
      id,
      title: `${target.label} sweet spot`,
      body: target.isBedtime
        ? `Start winding down — ${name}'s bedtime is around ${formatTime(target.start)}.`
        : `${name}'s ${target.label.toLowerCase()} window opens around ${formatTime(target.start)}.`,
      schedule: { at: fireAt },
      extra: { route: '/sleep' },
    });
  });

  await scheduleAll(plugin.api, notifications);
}

/**
 * Feed reminder: one notification `minutes` after the last feed (derived
 * from the last 24 h of events). Cleared and re-armed on every feed
 * mutation via the driver.
 */
export async function rescheduleFeedReminder({ lastFeedISO, enabled, minutes, babyName }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, FEED_REMINDER_ID, FEED_REMINDER_ID);
  if (!enabled || !lastFeedISO || !minutes) return;
  if (!(await checkPermission())) return;

  const prefs = getPrefs();
  const fireAt = new Date(new Date(lastFeedISO).getTime() + minutes * MINUTE);
  if (fireAt <= new Date()) return;
  if (isInQuietHours(fireAt, prefs)) return;

  await scheduleAll(plugin.api, [
    {
      id: FEED_REMINDER_ID,
      title: 'Feed reminder',
      body: `${babyName || 'Baby'}'s last feed was at ${formatTime(lastFeedISO)} — time for the next one?`,
      schedule: { at: fireAt },
      extra: { route: '/feed' },
    },
  ]);
}

/**
 * Medication dose reminders: one per medication with a future nextDoseAt,
 * fired at the dose time.
 */
export async function rescheduleMedReminders({ medications, enabled }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, MED_ID_START, MED_ID_END);
  if (!enabled || !Array.isArray(medications) || medications.length === 0) return;
  if (!(await checkPermission())) return;

  const prefs = getPrefs();
  const now = new Date();
  const notifications = [];
  medications
    .filter((m) => m.nextDoseAt && new Date(m.nextDoseAt) > now)
    .sort((a, b) => new Date(a.nextDoseAt) - new Date(b.nextDoseAt))
    .forEach((med, i) => {
      const id = MED_ID_START + i;
      if (id > MED_ID_END) return;
      const fireAt = new Date(med.nextDoseAt);
      if (isInQuietHours(fireAt, prefs)) return;
      notifications.push({
        id,
        title: 'Medication due',
        body: `${med.medicineName || 'Medication'} — next dose due at ${formatTime(med.nextDoseAt)}.`,
        schedule: { at: fireAt },
        extra: { route: '/medications' },
      });
    });

  await scheduleAll(plugin.api, notifications);
}

/**
 * Vaccination due reminders: one notification at 09:00 local on each
 * not-yet-completed vaccination due date falling within the next
 * VACC_WINDOW_DAYS days, capped at the 40-slot ID range. Due/done state is
 * derived from the baby's date of birth + recorded vaccinations via the
 * vaccination feature's own pure helpers, so this always agrees with the
 * schedule the Growth page shows.
 */
export async function rescheduleVaccinationReminders({ dob, vaccinations, enabled, babyName }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, VACC_ID_START, VACC_ID_END);
  if (!enabled || !dob) return;
  if (!(await checkPermission())) return;

  const prefs = getPrefs();
  const now = new Date();
  const horizon = new Date(now.getTime() + VACC_WINDOW_DAYS * DAY);
  const name = babyName || 'Baby';

  const notifications = [];
  matchVaccinationsToSchedule(nhsSchedule, vaccinations || [], dob)
    .filter((item) => item.status !== 'completed')
    .map((item) => {
      const fireAt = new Date(item.dueDate);
      fireAt.setHours(VACC_FIRE_HOUR, 0, 0, 0);
      return { item, fireAt };
    })
    .filter(({ fireAt }) => fireAt > now && fireAt <= horizon)
    .sort((a, b) => a.fireAt - b.fireAt)
    .forEach(({ item, fireAt }, i) => {
      const id = VACC_ID_START + i;
      if (id > VACC_ID_END) return; // cap at 40
      if (isInQuietHours(fireAt, prefs)) return;
      notifications.push({
        id,
        title: 'Vaccination due',
        body: `${item.name} is due today for ${name}.`,
        schedule: { at: fireAt },
        extra: { route: '/vaccinations' },
      });
    });

  await scheduleAll(plugin.api, notifications);
}

/**
 * Still-asleep check-in (9500, Premium): while a NAP is open, fire at
 * start + max(150 min, 1.5 × avgNapMinutes). Night sleeps never fire (the
 * 12 h timer guard covers a forgotten night timer). Quiet hours: skip.
 * `openSleep` is the open SLEEP event from useOpenSleep (or null).
 */
export async function rescheduleSleepCheckIn({ openSleep, avgNapMinutes, babyName, enabled }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, SLEEP_CHECKIN_ID, SLEEP_CHECKIN_ID);
  if (!enabled || !openSleep?.startTime) return;
  if (openSleep.data?.category === 'night') return;
  if (!(await checkPermission())) return;

  const start = toMs(openSleep.startTime);
  if (Number.isNaN(start)) return;
  const avg = Number(avgNapMinutes) || 0;
  const thresholdMinutes = Math.round(
    Math.max(SLEEP_CHECKIN_MIN_MINUTES, avg * SLEEP_CHECKIN_AVG_FACTOR)
  );
  const fireAt = new Date(start + thresholdMinutes * MINUTE);
  if (fireAt <= new Date()) return; // already past — the user is in the app
  if (isInQuietHours(fireAt, getPrefs())) return;

  await scheduleAll(plugin.api, [
    {
      id: SLEEP_CHECKIN_ID,
      title: 'Still asleep?',
      body: `${babyName || 'Baby'}'s nap has been running for ${formatDurationShort(thresholdMinutes)} — longer than usual. Tap to log the wake-up or check the timer.`,
      schedule: { at: fireAt },
      extra: { route: '/sleep' },
    },
  ]);
}

const TIMER_GUARD_COPY = {
  SLEEP: {
    id: TIMER_GUARD_SLEEP_ID,
    route: '/sleep',
    title: 'Sleep timer still running',
    body: (name, when) =>
      `${name}'s sleep timer started at ${when} and is still going. Stop it now to keep today's sleep total accurate.`,
  },
  FEED: {
    id: TIMER_GUARD_FEED_ID,
    route: '/feed',
    title: 'Feed timer still running',
    body: (name, when) => `The feed timer started at ${when} and is still going — stop it or discard it.`,
  },
  PUMPING: {
    id: TIMER_GUARD_PUMP_ID,
    route: '/pumping',
    title: 'Pumping timer still running',
    body: (name, when) => `The pumping timer started at ${when} and is still going — stop it or discard it.`,
  },
  ACTIVITY: {
    id: TIMER_GUARD_ACTIVITY_ID,
    route: '/activity',
    title: 'Activity timer still running',
    body: (name, when) => `The activity timer started at ${when} and is still going — stop it or discard it.`,
  },
};

/** "19:05" or "19:05 yesterday" relative to the fire time's local day. */
function startedAtLabel(startMs, fireAt) {
  const start = new Date(startMs);
  const sameDay = format(start, 'yyyy-MM-dd') === format(fireAt, 'yyyy-MM-dd');
  return sameDay ? formatTime(start) : `${formatTime(start)} yesterday`;
}

/**
 * Timer-left-running guards (9510–9513): the open sleep after 12 h, the
 * zustand FEED/FEED_DUAL/PUMPING timers after 2 h, ACTIVITY after 3 h.
 * Quiet hours: defer to the end of the window (a 19:00 sleep + 12 h lands at
 * 07:00 — exactly the default quiet end — so deferring is what makes this
 * useful). `activeTimer` is the zustand { type, startedAt } or null.
 */
export async function rescheduleTimerGuards({ openSleep, activeTimer, babyName, enabled }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, TIMER_GUARD_ID_START, TIMER_GUARD_ID_END);
  if (!enabled) return;

  const candidates = [];
  const sleepStart = toMs(openSleep?.startTime);
  if (openSleep && !Number.isNaN(sleepStart)) {
    candidates.push({ kind: 'SLEEP', startMs: sleepStart });
  }
  const timerType = activeTimer?.type;
  const timerStart = Number(activeTimer?.startedAt);
  if (timerType && timerType !== 'SLEEP' && Number.isFinite(timerStart) && timerStart > 0) {
    const kind = timerType === 'FEED_DUAL' ? 'FEED' : timerType;
    if (TIMER_GUARD_COPY[kind]) candidates.push({ kind, startMs: timerStart, type: timerType });
  }
  if (candidates.length === 0) return;
  if (!(await checkPermission())) return;

  const prefs = getPrefs();
  const now = new Date();
  const name = babyName || 'Baby';
  const notifications = [];
  candidates.forEach(({ kind, startMs, type }) => {
    const limit = TIMER_GUARD_LIMITS_MS[type || kind] || TIMER_GUARD_LIMITS_MS[kind];
    const copy = TIMER_GUARD_COPY[kind];
    const fireAt = deferPastQuietHours(new Date(startMs + limit), prefs);
    if (fireAt <= now) return; // already past — the user is in the app
    notifications.push({
      id: copy.id,
      title: copy.title,
      body: copy.body(name, startedAtLabel(startMs, fireAt)),
      schedule: { at: fireAt },
      extra: { route: copy.route },
    });
  });

  await scheduleAll(plugin.api, notifications);
}

/**
 * Nappy reminders (opt-in, and part of the baby-creation recommended set):
 * 9600 fires an age-aware gap after the last change (last 24 h); 9601 is a
 * once-a-day 16:00 hydration nudge scheduled only when a nappy has been
 * logged in the last 24 h AND today's wet+both count is below half the
 * expected number. Quiet hours: skip.
 */
export async function rescheduleNappyReminders({ lastNappyISO, wetTodayCount, dob, babyName, enabled }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, NAPPY_GAP_ID, HYDRATION_NUDGE_ID);
  if (!enabled) return;
  if (!(await checkPermission())) return;

  const prefs = getPrefs();
  const now = new Date();
  const name = babyName || 'Baby';
  const expected = getExpectedWetNappies(dob);
  const notifications = [];

  if (lastNappyISO) {
    const gapMinutes = Math.min(
      NAPPY_GAP_MAX_MINUTES,
      Math.max(NAPPY_GAP_MIN_MINUTES, Math.round((24 / expected) * 60 * 1.25))
    );
    const fireAt = new Date(new Date(lastNappyISO).getTime() + gapMinutes * MINUTE);
    if (fireAt > now && !isInQuietHours(fireAt, prefs)) {
      notifications.push({
        id: NAPPY_GAP_ID,
        title: 'Nappy check',
        body: `It's been ${formatDurationShort(gapMinutes)} since ${name}'s last nappy change — worth a check?`,
        schedule: { at: fireAt },
        extra: { route: '/nappy' },
      });
    }
  }

  // A LOW count is indistinguishable from "not logging nappies yet", so the
  // 16:00 nudge only arms once a nappy has been logged in the last 24 h — the
  // in-app HydrationMonitor holds back the same false concern. A brand-new
  // profile never gets a GP-flavoured alert on day one for empty logs.
  const wet = Number(wetTodayCount) || 0;
  if (lastNappyISO && getHydrationStatus(wet, expected) === 'concern') {
    const fireAt = new Date(now);
    fireAt.setHours(HYDRATION_NUDGE_HOUR, 0, 0, 0);
    if (fireAt > now && !isInQuietHours(fireAt, prefs)) {
      const logged =
        wet === 0
          ? `No wet nappies logged for ${name} yet today`
          : `Only ${wet} wet ${wet === 1 ? 'nappy' : 'nappies'} logged for ${name} so far today`;
      notifications.push({
        id: HYDRATION_NUDGE_ID,
        title: 'Hydration check',
        body: `${logged} — around ${expected} is typical by bedtime. Keep offering feeds, and contact your GP or health visitor if you're worried.`,
        schedule: { at: fireAt },
        extra: { route: '/nappy' },
      });
    }
  }

  await scheduleAll(plugin.api, notifications);
}

/**
 * Pumping reminder (9700, opt-in): `minutes` after the last pump finished
 * (last 24 h). Quiet hours: skip — exclusive pumpers who want overnight
 * reminders turn quiet hours off.
 */
export async function reschedulePumpReminder({ lastPumpISO, enabled, minutes }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, PUMP_REMINDER_ID, PUMP_REMINDER_ID);
  if (!enabled || !lastPumpISO || !minutes) return;
  if (!(await checkPermission())) return;

  const fireAt = new Date(new Date(lastPumpISO).getTime() + minutes * MINUTE);
  if (fireAt <= new Date()) return;
  if (isInQuietHours(fireAt, getPrefs())) return;

  await scheduleAll(plugin.api, [
    {
      id: PUMP_REMINDER_ID,
      title: 'Pumping reminder',
      body: `Your last pump finished at ${formatTime(lastPumpISO)} — time for the next session?`,
      schedule: { at: fireAt },
      extra: { route: '/pumping' },
    },
  ]);
}

/**
 * Today's totals for the recap body. `now` bounds the open sleep's elapsed time.
 * Exported so the driver can key its effect on primitives.
 */
export function summariseDay(todayEvents, now = Date.now()) {
  let feeds = 0;
  let nappies = 0;
  let sleepMinutes = 0;
  (todayEvents || []).forEach((e) => {
    const type = eventType(e);
    if (type === 'FEED') feeds += 1;
    else if (type === 'NAPPY') nappies += 1;
    else if (isSleep(e)) sleepMinutes += sleepDurationMinutes(e, now);
  });
  return { feeds, nappies, sleepMinutes };
}

/**
 * Daily evening recap (9800) at the user-chosen `time` ('HH:mm'), today only
 * — the body is a static snapshot of "so far", refreshed every time the
 * driver runs, so a recap for tomorrow with today's numbers is never armed.
 * Skipped when today has no events. User-chosen times bypass quiet hours.
 * `schedule` (Premium, optional) adds the predicted bedtime while it is ahead.
 */
export async function rescheduleDailyRecap({ enabled, time, feeds, nappies, sleepMinutes, babyName, schedule }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, DAILY_RECAP_ID, DAILY_RECAP_ID);
  if (!enabled) return;
  const total = (Number(feeds) || 0) + (Number(nappies) || 0) + (Number(sleepMinutes) || 0);
  if (total <= 0) return; // nothing logged today — never send "0 feeds, 0 sleep"
  if (!(await checkPermission())) return;

  const now = new Date();
  const fireAt = atLocalTime(now, time, { hour: 20, minute: 0 });
  if (fireAt <= now) return;

  const name = babyName || 'Baby';
  const parts = [
    `${feeds || 0} ${feeds === 1 ? 'feed' : 'feeds'}`,
    `${formatDurationShort(sleepMinutes || 0)} sleep`,
    `${nappies || 0} ${nappies === 1 ? 'nappy' : 'nappies'}`,
  ];
  let body = `${parts.join(' · ')} so far. Tap for today's timeline.`;
  const bedtime = toMs(schedule?.bedtime?.start);
  if (!Number.isNaN(bedtime) && !schedule?.bedtime?.taken && bedtime > fireAt.getTime()) {
    body += ` Bedtime is predicted around ${formatTime(schedule.bedtime.start)}.`;
  }

  await scheduleAll(plugin.api, [
    {
      id: DAILY_RECAP_ID,
      title: `${name}'s day so far`,
      body,
      schedule: { at: fireAt },
      extra: { route: '/' },
    },
  ]);
}

function readTipsSeen() {
  try {
    const raw = localStorage.getItem(TIPS_SEEN_KEY);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

/** Deterministic fallback pick if the tips helper is unavailable or throws. */
function fallbackTip(tips, ageWeeks, dateISO, babyId) {
  const inBand = tips.filter(
    (t) =>
      ageWeeks === null ||
      ((t.ageWeeksMin ?? 0) <= ageWeeks && (t.ageWeeksMax ?? Infinity) >= ageWeeks)
  );
  const pool = inBand.length ? inBand : tips;
  if (pool.length === 0) return null;
  const key = `${dateISO}${babyId || ''}`;
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return pool[hash % pool.length];
}

/**
 * Daily tip (9810–9816): the next 7 fire times at the user-chosen `time`,
 * each announcing the tip `pickTipOfTheDay` picks for that date (so the
 * dashboard card shows the same tip that morning). Bypasses quiet hours.
 */
export async function rescheduleDailyTips({ enabled, time, dob, babyId }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, DAILY_TIP_ID_START, DAILY_TIP_ID_END);
  if (!enabled) return;
  if (!(await checkPermission())) return;

  const tips = Array.isArray(tipsCatalogue?.tips) ? tipsCatalogue.tips : [];
  if (tips.length === 0) return;
  const categoryLabel = {};
  (tipsCatalogue?.categories || []).forEach((c) => {
    categoryLabel[c.key] = c.label;
  });
  const seen = readTipsSeen();

  const now = new Date();
  let first = atLocalTime(now, time, { hour: 9, minute: 0 });
  if (first <= now) first = addDays(first, 1);

  const notifications = [];
  for (let i = 0; i < DAILY_TIP_DAYS; i++) {
    const id = DAILY_TIP_ID_START + i;
    if (id > DAILY_TIP_ID_END) break;
    const fireAt = addDays(first, i);
    const dateISO = format(fireAt, 'yyyy-MM-dd');
    const ageWeeks = ageWeeksAt(dob, fireAt);
    let tip = null;
    try {
      tip = pickTipOfTheDay({ tips, ageWeeks, seen, dateISO, babyId });
    } catch {
      tip = null;
    }
    if (!tip) tip = fallbackTip(tips, ageWeeks, dateISO, babyId);
    if (!tip?.id || !tip.title) continue;
    const label = categoryLabel[tip.category] || 'Tips';
    notifications.push({
      id,
      title: `Tip of the day · ${label}`,
      body: tip.title,
      schedule: { at: fireAt },
      extra: { route: `/tips/${tip.id}`, tipId: tip.id },
    });
  }

  await scheduleAll(plugin.api, notifications);
}

/** NHS weighing cadence, simplified: < 6 mo 28 d · 6–12 mo 56 d · 12 mo+ 90 d. */
function growthIntervalDays(dob, at) {
  const ageDays = differenceInDays(at, new Date(dob));
  if (ageDays < 183) return 28;
  if (ageDays < 365) return 56;
  return 90;
}

/**
 * Growth check-in (9900): 10:00 local on lastMeasurement + interval. When
 * already overdue (or never measured) it is armed once for tomorrow 10:00
 * and remembered per measurement gap, so it does not repeat daily. Quiet
 * hours: defer. `records` are the growth rows (`date || createdAt`).
 */
export async function rescheduleGrowthCheckIn({ records, dob, babyId, babyName, enabled }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, GROWTH_CHECKIN_ID, GROWTH_CHECKIN_ID);
  if (!enabled || !dob) return;
  if (!(await checkPermission())) return;

  const prefs = getPrefs();
  const now = new Date();
  const name = babyName || 'Baby';
  const lastMs = (Array.isArray(records) ? records : [])
    .map((r) => toMs(r?.date || r?.createdAt))
    .filter((ms) => !Number.isNaN(ms))
    .sort((a, b) => b - a)[0];
  const hasMeasurement = Number.isFinite(lastMs);
  const lastKey = hasMeasurement ? new Date(lastMs).toISOString() : 'none';
  const intervalDays = growthIntervalDays(dob, now);

  let fireAt = hasMeasurement ? new Date(lastMs + intervalDays * DAY) : addDays(new Date(dob), intervalDays);
  fireAt.setHours(GROWTH_FIRE_HOUR, 0, 0, 0);

  const markerKey = `${GROWTH_NUDGE_KEY_PREFIX}${babyId || 'baby'}`;
  if (fireAt <= now) {
    // Overdue: one nudge for tomorrow 10:00, then stay quiet until a new
    // measurement changes `lastKey` (never a daily nag).
    let marker = null;
    try {
      marker = localStorage.getItem(markerKey);
    } catch {
      marker = null;
    }
    let promisedMs = null;
    if (marker) {
      const [markedKey, markedISO] = marker.split('|');
      const markedMs = toMs(markedISO);
      if (markedKey === lastKey && !Number.isNaN(markedMs)) promisedMs = markedMs;
    }
    if (promisedMs !== null) {
      // Already fired for this measurement gap: stay quiet until a new
      // measurement changes `lastKey`.
      if (promisedMs <= now.getTime()) return;
      // Armed but not yet fired. `cancelRange` above has just removed it, so it
      // must be re-armed at THE SAME INSTANT. Recomputing "tomorrow 10:00" here
      // pushed it forward a day on every launch before 10:00 — near-universal
      // for a baby app — so the check-in never actually fired.
      fireAt = new Date(promisedMs);
    } else {
      fireAt = addDays(now, 1);
      fireAt.setHours(GROWTH_FIRE_HOUR, 0, 0, 0);
      try {
        localStorage.setItem(markerKey, `${lastKey}|${fireAt.toISOString()}`);
      } catch {
        /* storage unavailable — worst case it repeats tomorrow */
      }
    }
  }
  fireAt = deferPastQuietHours(fireAt, prefs);
  if (fireAt <= now) return;

  const sinceDays = hasMeasurement ? Math.max(1, differenceInDays(now, new Date(lastMs))) : 0;
  const since =
    sinceDays >= 14 ? `${Math.round(sinceDays / 7)} weeks` : `${sinceDays} ${sinceDays === 1 ? 'day' : 'days'}`;
  const body = hasMeasurement
    ? `It's been ${since} since ${name}'s last measurement. Add a weight or length to keep the chart up to date.`
    : `No measurements yet for ${name} — add a weight or length to start the growth chart.`;

  await scheduleAll(plugin.api, [
    {
      id: GROWTH_CHECKIN_ID,
      title: 'Growth check-in',
      body,
      schedule: { at: fireAt },
      extra: { route: '/growth' },
    },
  ]);
}

const HEALTH_REVIEWS = [
  {
    id: HEALTH_REVIEW_ID_START,
    at: (dob) => addDays(new Date(dob), 42),
    body: (name) =>
      `${name} is nearly 6 weeks old — the NHS 6–8 week review with your GP or health visitor is due soon.`,
  },
  {
    id: HEALTH_REVIEW_ID_START + 1,
    at: (dob) => addMonths(new Date(dob), 9),
    body: (name) =>
      `${name} is nearly 9 months old — the NHS 9–12 month review with your health visitor is due soon.`,
  },
  {
    id: HEALTH_REVIEW_ID_START + 2,
    at: (dob) => addMonths(new Date(dob), 24),
    body: (name) =>
      `${name} is nearly 2 — the NHS 2 to 2½ year review with your health visitor is due soon.`,
  },
];

/**
 * NHS health-review heads-up (9910–9913): 09:00 local, 3 days before the
 * baby turns 6 weeks, 9 months and 24 months — from the date of birth only.
 * Lives under the vaccinations toggle. Quiet hours: defer.
 */
export async function rescheduleHealthReviews({ dob, babyName, enabled }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, HEALTH_REVIEW_ID_START, HEALTH_REVIEW_ID_END);
  if (!enabled || !dob || Number.isNaN(toMs(dob))) return;
  if (!(await checkPermission())) return;

  const prefs = getPrefs();
  const now = new Date();
  const name = babyName || 'Baby';
  const notifications = [];
  HEALTH_REVIEWS.forEach((review) => {
    if (review.id > HEALTH_REVIEW_ID_END) return;
    let fireAt = addDays(review.at(dob), -HEALTH_REVIEW_LEAD_DAYS);
    fireAt.setHours(HEALTH_REVIEW_FIRE_HOUR, 0, 0, 0);
    fireAt = deferPastQuietHours(fireAt, prefs);
    if (fireAt <= now) return;
    notifications.push({
      id: review.id,
      title: 'Health review coming up',
      body: review.body(name),
      schedule: { at: fireAt },
      extra: { route: '/vaccinations' },
    });
  });

  await scheduleAll(plugin.api, notifications);
}

/**
 * Weekly wellbeing check-in (9950, opt-in): next Sunday 20:30 local (today,
 * if it is Sunday and 20:30 is still ahead — unless a mood entry already
 * exists for today). Quiet hours: skip.
 */
export async function rescheduleWellbeingCheckIn({ enabled, moodLoggedToday }) {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, MOOD_CHECKIN_ID, MOOD_CHECKIN_ID);
  if (!enabled) return;
  if (!(await checkPermission())) return;

  const now = new Date();
  const fireAt = new Date(now);
  fireAt.setHours(MOOD_CHECKIN_TIME.hour, MOOD_CHECKIN_TIME.minute, 0, 0);
  let daysAhead = (MOOD_CHECKIN_DAY - now.getDay() + 7) % 7;
  if (daysAhead === 0 && (fireAt <= now || moodLoggedToday)) daysAhead = 7;
  fireAt.setDate(fireAt.getDate() + daysAhead);
  if (fireAt <= now) return;
  if (isInQuietHours(fireAt, getPrefs())) return;

  await scheduleAll(plugin.api, [
    {
      id: MOOD_CHECKIN_ID,
      title: 'How are you doing?',
      body: 'A 10-second mood check-in helps you notice your own patterns. Your wellbeing matters too.',
      schedule: { at: fireAt },
      extra: { route: '/mood' },
    },
  ]);
}

// --- Trial offer (9960, v1.5.0) — one-off, driven by features/offer/trialOffer.js ---

export const TRIAL_OFFER_DELAY_MINUTES = 30;
// The trial length the copy quotes when StoreKit has not said otherwise — the
// static fallback of docs/v150-spec.md §0.
export const TRIAL_OFFER_FALLBACK_DAYS = 7;

/**
 * When the trial offer fires: TRIAL_OFFER_DELAY_MINUTES after `now`, moved to
 * the end of the quiet-hours window when it would land inside it ('defer' — a
 * paywall closed at 23:40 must not buzz at 00:10). Pure.
 */
export function trialOfferFireTime(now = new Date(), prefs = getPrefs()) {
  const due = new Date(new Date(now).getTime() + TRIAL_OFFER_DELAY_MINUTES * MINUTE);
  return deferPastQuietHours(due, prefs);
}

/** The 9960 notification itself (pure). `trialDays` falls back to 7. */
export function buildTrialOfferNotification({ babyName, trialDays, fireAt }) {
  const name = String(babyName || '').trim();
  const days = Number.isInteger(trialDays) && trialDays > 0 ? trialDays : TRIAL_OFFER_FALLBACK_DAYS;
  return {
    id: TRIAL_OFFER_ID,
    title: name ? `A little something for you and ${name}` : 'A little something for you',
    body: `Try BabyTrack Premium free for ${days} days — sleep predictions, Ask Atlas and more.`,
    schedule: { at: fireAt },
    extra: { route: '/offer' },
  };
}

/**
 * Schedule the one-off trial offer, replacing any pending 9960. Checks the
 * permission silently — never prompts. Resolves
 *   { scheduled: true } | { scheduled: false, reason: 'unsupported' | 'no-permission' | 'error' }
 * The offers pref, the plan and the once-per-user rule belong to the caller
 * (features/offer/trialOffer.js).
 */
export async function scheduleTrialOfferNotification({ babyName, trialDays, fireAt }) {
  const plugin = await getPlugin();
  if (!plugin) return { scheduled: false, reason: 'unsupported' };
  if (!(await checkPermission())) return { scheduled: false, reason: 'no-permission' };
  const at = fireAt instanceof Date ? fireAt : trialOfferFireTime();
  try {
    await plugin.api.schedule({
      notifications: [buildTrialOfferNotification({ babyName, trialDays, fireAt: at })],
    });
    return { scheduled: true };
  } catch {
    return { scheduled: false, reason: 'error' };
  }
}

/**
 * Cancel a pending trial offer and clear a delivered one from Notification
 * Centre, so it can never be tapped by whoever uses the app next (sign-out,
 * account deletion, the offers toggle, a purchase).
 */
export async function cancelTrialOfferNotification() {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, TRIAL_OFFER_ID, TRIAL_OFFER_ID);
  try {
    await plugin.api.removeDeliveredNotifications({ notifications: [{ id: TRIAL_OFFER_ID }] });
  } catch {
    /* nothing delivered */
  }
}

// --- Second ask (9961, v1.5.1, docs/v151-spec.md §2.6) — one-off, scheduled with 9960 ---

// Day 1 is the day of sign-up, so day 8 is 7 calendar days later.
const SECOND_ASK_AFTER_DAYS = 7;
const SECOND_ASK_TIME = '10:00';
const SECOND_ASK_ROUTE = '/paywall?src=day8-notification&reason=week';

/**
 * When the second ask fires: 7 days after `createdAt` at 10:00 local, moved to
 * the end of the quiet-hours window when a user's quiet hours cover 10:00
 * ('defer'). Calendar days, so a clock change during the week keeps the day.
 * Pure.
 */
export function secondAskFireTime(createdAt = new Date(), prefs = getPrefs()) {
  const day = addDays(new Date(createdAt), SECOND_ASK_AFTER_DAYS);
  return deferPastQuietHours(atLocalTime(day, SECOND_ASK_TIME, { hour: 10, minute: 0 }), prefs);
}

/** The 9961 notification itself (pure). `trialDays` falls back to 7, like 9960. */
export function buildSecondAskNotification({ babyName, trialDays, fireAt }) {
  const name = String(babyName || '').trim();
  const days = Number.isInteger(trialDays) && trialDays > 0 ? trialDays : TRIAL_OFFER_FALLBACK_DAYS;
  return {
    id: SECOND_ASK_ID,
    title: name ? `${name}'s first week is in BabyTrack` : 'Your first week is in BabyTrack',
    body: `Free shows the last 7 days. See every day with Premium — try it free for ${days} days.`,
    schedule: { at: fireAt },
    extra: { route: SECOND_ASK_ROUTE },
  };
}

/**
 * Schedule the one-off second ask, replacing any pending 9961. Checks the
 * permission silently — never prompts. Resolves
 *   { scheduled: true } | { scheduled: false, reason: 'unsupported' | 'no-permission' | 'error' }
 * As with 9960, the offers pref, the plan and the once-per-user rule belong to
 * the caller (features/offer/trialOffer.js, which schedules it after 9960).
 */
export async function scheduleSecondAskNotification({ babyName, trialDays, fireAt }) {
  const plugin = await getPlugin();
  if (!plugin) return { scheduled: false, reason: 'unsupported' };
  if (!(await checkPermission())) return { scheduled: false, reason: 'no-permission' };
  const at = fireAt instanceof Date ? fireAt : secondAskFireTime();
  try {
    await plugin.api.schedule({
      notifications: [buildSecondAskNotification({ babyName, trialDays, fireAt: at })],
    });
    return { scheduled: true };
  } catch {
    return { scheduled: false, reason: 'error' };
  }
}

/**
 * Cancel a pending second ask and clear a delivered one from Notification
 * Centre: with the trial offer (cancelTrialOffer — sign-out, account deletion,
 * the offers toggle, a purchase), and by useSecondAsk once the in-app ask has
 * shown — the notification would only repeat it.
 */
export async function cancelSecondAskNotification() {
  const plugin = await getPlugin();
  if (!plugin) return;
  await cancelRange(plugin.api, SECOND_ASK_ID, SECOND_ASK_ID);
  try {
    await plugin.api.removeDeliveredNotifications({ notifications: [{ id: SECOND_ASK_ID }] });
  } catch {
    /* nothing delivered */
  }
}

/** Age guard shared with the widget: an "open" sleep older than 16 h is stale. */
export function isRecentOpenSleepStart(startISO, now = Date.now()) {
  const start = toMs(startISO);
  return !Number.isNaN(start) && now - start < OPEN_SLEEP_MAX_AGE_MS;
}
